"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PunchModal = exports.PUNCH_MODAL_SPINNER_TEST_ID = void 0;
const react_1 = __importDefault(require("react"));
const react_i18next_1 = require("react-i18next");
const luxon_1 = require("luxon");
const Row_1 = require("@amzn/atoz-web/Row");
const Button_1 = require("@amzn/atoz-web/Button");
const Modal_1 = require("@amzn/atoz-web/Modal");
const Spinner_1 = require("@amzn/atoz-web/Spinner");
const Text_1 = require("@amzn/atoz-web/Text");
const View_1 = require("@amzn/atoz-web/View");
const Datepicker_1 = require("@amzn/atoz-web/Datepicker");
const Checkbox_1 = require("@amzn/atoz-web/Checkbox");
const MessageBanner_1 = require("@amzn/atoz-web/MessageBanner");
const Col_1 = require("@amzn/atoz-web/Col");
const Theme_1 = require("@amzn/atoz-web/Theme");
const PunchModalTypes_1 = require("./PunchModalTypes");
const PunchErrorModal_1 = require("../PunchErrorModal");
const TextTimeInput_1 = require("../TextTimeInput");
const DateManagement_1 = require("../../utils/DateManagement");
const usePunchModal_1 = require("./hooks/usePunchModal");
const logger_1 = require("../../logger");
const consts_1 = require("../../consts");
const PunchFeature_1 = require("../../features/Punch/PunchFeature");
exports.PUNCH_MODAL_SPINNER_TEST_ID = 'punch-modal-spinner';
const PunchModal = ({ ctkPolicy, currentSelectedDate, defaultTime, militaryTime, close, submit, associateInfo, modalType, hasLimit, payPeriodBreakdownInfo }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const usageLocation = consts_1.componentNames.PUNCH_MODAL.WEB;
    const { modalTitleText, modalMessageText, calendarTitleText, timeInputLabelText, submissionWarningText, submitButtonText, isLockoutStatusLoading, lockoutStatusError, lockoutStatus, selectedDate, setSelectedDate, punchTime, setPunchTime, inputError, setInputError, inputErrorMessage, setInputErrorMessage, useMilitaryTime, toggleMilitaryTime } = (0, usePunchModal_1.usePunchModal)({
        associateInfo,
        ctkPolicy,
        currentSelectedDate,
        defaultTime,
        militaryTime,
        modalType,
        hasLimit,
        usageLocation
    });
    const metricName = modalType === PunchModalTypes_1.PunchModalType.MISSED_PUNCH
        ? logger_1.METRICS.MISSED_PUNCH_MODAL
        : modalType === PunchModalTypes_1.PunchModalType.PUNCH_CORRECTION
            ? logger_1.METRICS.PUNCH_CORRECTION_MODAL
            : logger_1.METRICS.PUNCH_EDIT_MODAL;
    (0, logger_1.useComponentLoadedMetrics)({
        metricNames: {
            countMetricName: metricName.COUNT
        },
        isComponentLoaded: !isLockoutStatusLoading
    });
    const validatePunchTime = (punchTime) => {
        return useMilitaryTime
            ? !/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/gm.test(punchTime)
            : !/^(1[0-2]|0?[1-9]):[0-5][0-9] (am|pm)$/gm.test(punchTime);
    };
    const validate = () => {
        const isInvalid = validatePunchTime(punchTime || '');
        if (isInvalid) {
            setInputError(true);
            const errorMessage = modalType === PunchModalTypes_1.PunchModalType.PUNCH_EDIT
                ? t('time_and_attendance_PunchModal_PunchEditError_Time')
                : t('time_and_attendance_PunchModal_Error_Time');
            setInputErrorMessage(errorMessage);
            return false;
        }
        const parsedPunchTime = (0, DateManagement_1.parsePunchTime)(punchTime || '', selectedDate, useMilitaryTime);
        if (parsedPunchTime > luxon_1.DateTime.now()) {
            setInputError(true);
            const errorMessage = modalType === PunchModalTypes_1.PunchModalType.PUNCH_EDIT
                ? t('time_and_attendance_PunchModal_PunchEditError_Future')
                : t('time_and_attendance_PunchModal_Error_Future');
            setInputErrorMessage(errorMessage);
            return false;
        }
        setInputError(false);
        return true;
    };
    const firstUpdate = react_1.default.useRef(true);
    react_1.default.useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        validate();
    }, [selectedDate, punchTime]);
    const handleSubmit = () => {
        const isValid = validate();
        if (!isValid) {
            const input = document.getElementById('text-time-input');
            input === null || input === void 0 ? void 0 : input.focus();
            return;
        }
        submit(punchTime || '', selectedDate, useMilitaryTime);
    };
    const renderModalButtons = () => {
        if (isLockoutStatusLoading) {
            return [];
        }
        const buttons = (react_1.default.createElement(Row_1.Row, { key: "PunchModalButtonRow", justifyContent: "space-between", width: '100%' },
            react_1.default.createElement(Button_1.Button, { key: "PunchModalCancelButton", dataTestId: "punch-modal-cancel-button", onClick: close, variant: Button_1.ButtonVariant.Tertiary, "aria-label": t('time_and_attendance_Cancel') }, t('time_and_attendance_Cancel')),
            react_1.default.createElement(Button_1.Button, { key: "PunchModalSubmitButton", dataTestId: "punch-modal-submit-button", onClick: handleSubmit, variant: Button_1.ButtonVariant.Primary, disabled: hasLimit && lockoutStatus.type === PunchModalTypes_1.LockoutStatusType.LOCKED_OUT, "aria-label": submitButtonText }, submitButtonText)));
        return [buttons];
    };
    const renderMessage = () => modalMessageText && (react_1.default.createElement(Text_1.Text, { fontSize: "T200", fontWeight: "light" }, modalMessageText));
    const renderLockoutMessage = () => {
        if (hasLimit && lockoutStatus.type !== PunchModalTypes_1.LockoutStatusType.OK) {
            const bannerType = lockoutStatus.type === PunchModalTypes_1.LockoutStatusType.WARNING
                ? MessageBanner_1.MessageBannerType.Warning
                : MessageBanner_1.MessageBannerType.Error;
            return (react_1.default.createElement(MessageBanner_1.MessageBanner, { dataTestId: "punch-modal-aip-warning-banner", iconAltText: t('time_and_attendance_Warning'), type: bannerType }, lockoutStatus.message));
        }
    };
    const renderSubmissionWarning = () => submissionWarningText && (react_1.default.createElement(Text_1.Text, { fontSize: "T200", fontWeight: "light" }, submissionWarningText));
    const textTimeInputClassName = 'time-input form-control' + (!!punchTime ? ' filled' : ' ');
    const textTimeInputClassNameError = 'time-input form-control error';
    const isDateEnabled = (date) => {
        return ((modalType === PunchModalTypes_1.PunchModalType.MISSED_PUNCH &&
            (0, PunchFeature_1.isWithinPrevAndCurrPayPeriod)(luxon_1.DateTime.fromISO(date.toISOString()), payPeriodBreakdownInfo)) ||
            modalType !== PunchModalTypes_1.PunchModalType.MISSED_PUNCH);
    };
    const renderPunchDatePicker = () => (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(Text_1.Label, null, calendarTitleText),
        react_1.default.createElement(Datepicker_1.HamiltonDatepicker, { dataTestId: "PunchModalDatePicker", selected: (0, DateManagement_1.getLocalJSDate)(selectedDate), inline: true, locale: luxon_1.Settings.defaultLocale, showDisabledMonthNavigation: true, formatWeekDay: (day) => day.substring(0, 3).toLocaleUpperCase(), onChange: (date) => setSelectedDate((0, DateManagement_1.createDTDayInDefaultTZ)(date)), headerTextProps: { fontSize: 'T400' }, filterDate: isDateEnabled, overrideTodayDate: (0, DateManagement_1.getLocalJSDate)(luxon_1.DateTime.now()), previousMonthAriaLabel: t('time_and_attendance_DatePicker_prevButton'), nextMonthAriaLabel: t('time_and_attendance_DatePicker_nextButton') })));
    const renderTimeInput = () => (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(StyledLabel, { htmlFor: "punch-modal-time-input" }, timeInputLabelText),
        react_1.default.createElement(View_1.View, { className: "atoz-filter-text-time-input" },
            react_1.default.createElement(TextTimeInput_1.TextTimeInput, { onChange: setPunchTime, className: inputError ? textTimeInputClassNameError : textTimeInputClassName, value: punchTime || '', placeholder: t('time_and_attendance_PunchModal_EnterTime'), militaryTime: useMilitaryTime, dataTestId: "punch-modal-time-input" }),
            inputError ? (react_1.default.createElement(MessageBanner_1.MessageBanner, { dataTestId: "text-time-input-footer", iconAltText: t('time_and_attendance_Error'), type: MessageBanner_1.MessageBannerType.Error }, inputErrorMessage)) : ('')),
        react_1.default.createElement(Row_1.Row, { alignItems: "center", gridGap: "S200", margin: { top: 'S400' } },
            react_1.default.createElement(View_1.View, { onClick: toggleMilitaryTime },
                react_1.default.createElement(Checkbox_1.Checkbox, { dataTestId: "punch-military-time-checkbox", id: "punch-military-time-checkbox", checked: !useMilitaryTime })),
            react_1.default.createElement(Text_1.Label, { htmlFor: "punch-military-time-checkbox" }, t('time_and_attendance_PunchModal_MissedPunchUseMilitaryTime')))));
    const renderPunchErrorModal = () => (react_1.default.createElement(Modal_1.Modal, { isOpen: lockoutStatusError, close: close },
        react_1.default.createElement(PunchErrorModal_1.PunchErrorModal, { retry: close, close: close })));
    return (react_1.default.createElement(Modal_1.ModalContent, { dataTestId: "PunchModal", titleText: modalTitleText, buttons: renderModalButtons(), onCloseButtonClick: close, closeButtonAltText: t('time_and_attendance_PunchModal_Close') }, isLockoutStatusLoading ? (react_1.default.createElement(SpinnerContainer, { dataTestId: exports.PUNCH_MODAL_SPINNER_TEST_ID },
        react_1.default.createElement(Spinner_1.Spinner, { size: Spinner_1.SpinnerSize.Large }))) : (react_1.default.createElement(Col_1.Col, { gridGap: "S300" },
        renderMessage(),
        renderLockoutMessage(),
        renderPunchDatePicker(),
        renderTimeInput(),
        renderSubmissionWarning(),
        renderPunchErrorModal()))));
};
exports.PunchModal = PunchModal;
const StyledLabel = (0, Theme_1.styledWithTheme)(Text_1.Label)(`
  font-weight: 500;
`);
const SpinnerContainer = (0, Theme_1.styledWithTheme)(View_1.View)(`
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    width: min(calc(100vw - 64px), 480px);
    height: calc(100vh - 320px);
  `);
